import React from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

// styles
import styles from './styles-desktop.module.css';
// config
import {
  ELEMENT_TYPE,
  THEME,
  NAVIGATION_FROM,
  YEAR_ANIMATIONS_DIRECTION,
} from '../config/constants';
// data
import timelineData from '../data/timeline';
// local components
import YearComponent from './components-desktop/YearComponent';
import Moment from './components-desktop/MomentComponent';
import Designer from './components-desktop/DesignersComponent';
import Product from './components-desktop/ProductComponent';
import Menu from './components-desktop/MenuComponent';
import Room from './components-desktop/RoomComponent';
import RoomPreview from './components-desktop/RoomPreviewComponent';
import DiscoverComponent from './components-desktop/DiscoverComponent';
import DesignComponent from './components-desktop/DesignComponent';

import HeaderBraun from '../common/HeaderBraun';

// spa loader
import ComponentLoader from '../SpaComponentLoader';

import normalizeWheel from '../helpers/normalizeScroll';

export default class Timeline extends React.Component {
  constructor(props) {
    super(props);
    // load component in SPA mode based on URL

    this.enableMarkers = false;
    this.pinnedDesigners = [];
    this.designersPerYear = {};

    this.availableYearsArray = timelineData
      .filter((timelineElem) => timelineElem.type === ELEMENT_TYPE.YEAR)
      .map((yearData) => parseInt(yearData.year, 10))
      .sort();

    this.state = {
      showSpaComponentLoader: false,
      currentComponentType: null,
      currentComponentData: null,
    };
  }

  componentDidMount() {
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
    // enable drag
    this.enableDrag();
    // enable mouse wheel scroll
    this.enableScrollWheel('.timelineWrapper');
    // [Animations on ScrollTrigger]
    this.animateYears();
    this.animateMoments();
    this.animateProducts();
    this.animateRooms();
    this.animateDesigners();
    this.animateRoomPreview();
    this.animateDesignElements();
  }

  alignDesigners(force) {
    // move designer to the left, so all of them are visible
    this.pinnedDesigners.forEach((pinnedDesigner, index) => {
      gsap[force ? 'set' : 'to'](pinnedDesigner, {
        marginLeft: `${3.6458333333333335 * index}vw`,
        position: 'absolute',
        zIndex: 300 + index,
        ease: 'power2.inOut',
        force3D: true,
      });
    });
  }

  updateScrollTriggers() {
    const animatedElements = [
      '.product-elem',
      '.year-elem',
      '.moment-elem',
      '.designer-elem',
      '.design-elem',
      '.root-preview-elem',
    ];

    animatedElements.forEach((animatedElemClass) => {
      gsap.utils.toArray(animatedElemClass).forEach((elem) => {
        const scrollTriggerId = elem.getAttribute('id');
        if (scrollTriggerId && ScrollTrigger.getById(scrollTriggerId)) {
          ScrollTrigger.getById(scrollTriggerId).refresh();
        }
      });
    });

    this.alignDesigners(true);
  }

  closestEndYear(year) {
    return this.availableYearsArray.reduce((a, b) => {
      return Math.abs(b - year) < Math.abs(a - year) ? b : a;
    });
  }

  navigateToYear(year) {
    gsap.to('.timelineWrapper', {
      duration: 2,
      scrollTo: { x: `#year-${year}`, offsetX: 150 },
    });
  }

  handleMenu(isMenuOpened) {
    gsap.to('.timelineWrapper', {
      x: !isMenuOpened ? '100vw' : 0,
      duration: 0.5,
      ease: 'linear',
    });
  }

  openComponentInSpaMode(type, data) {
    // show component in SPA mode
    this.setState({
      currentComponentType: type,
      currentComponentData: data,
      showSpaComponentLoader: true,
    });
  }

  animateYears() {
    // animate years
    gsap.utils.toArray('.year-elem').forEach((year) => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          horizontal: true,
          trigger: year,
          toggleActions: 'play reverse play reverse',
          start: 'center 92%',
          end: 'center 8%',
          markers: false,
          id: year.getAttribute('id'),
          onEnter: () => year.classList.add(YEAR_ANIMATIONS_DIRECTION.FORWARD),
          onEnterBack: () =>
            year.classList.add(YEAR_ANIMATIONS_DIRECTION.BACKWARD),
          onLeave: () =>
            year.classList.remove(
              YEAR_ANIMATIONS_DIRECTION.FORWARD,
              YEAR_ANIMATIONS_DIRECTION.BACKWARD,
            ),
          onLeaveBack: () =>
            year.classList.remove(
              YEAR_ANIMATIONS_DIRECTION.FORWARD,
              YEAR_ANIMATIONS_DIRECTION.BACKWARD,
            ),
        },
      });
      // year number
      timeline.from(
        year.querySelector('.yearNumber'),
        0.2,
        {
          autoAlpha: 0,
          scale: 0.6,
          transformOrigin: 'bottom center',
          ease: 'power2.inOut',
        },
        0,
      );
      // // year icon
      // timeline.from(
      //   year.querySelector('.yearTimelineIcon'),
      //   0.2,
      //   { autoAlpha: 0, ease: 'power2.inOut' },
      //   0,
      // );
    });
  }

  animateMoments() {
    // animate moments
    gsap.utils.toArray('.moment-elem').forEach((moment) => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          horizontal: true,
          trigger: moment,
          toggleActions: 'play reverse play reverse',
          start: 'center 92%',
          end: 'center 8%',
          markers: false,
          id: moment.getAttribute('id'),
        },
      });

      //  moments & designers & room desc
      timeline.from(
        moment.querySelector('.desc'),
        0.2,
        {
          autoAlpha: 0,
          scale: 0.4,
          y: '-1.0416666666666667vw',
          x: '1.1458333333333333vw',
          transformOrigin: '0 0',
          ease: 'power2.inOut',
        },
        0,
      );
      //  moments & designers & room icon
      timeline.from(
        moment.querySelector('.icon'),
        0.2,
        {
          scale: 0.4,
          // autoAlpha: 0,
          transformOrigin: '50% 54%',
          ease: 'power2.inOut',
        },
        0,
      );
    });
  }

  animateProducts() {
    // animate products
    gsap.utils.toArray('.product-elem').forEach((product) => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          horizontal: true,
          trigger: product,
          toggleActions: 'play reverse play reverse',
          start: 'left 92%',
          end: 'left 8%',
          markers: false,
          id: product.getAttribute('id'),
        },
      });
      // product item
      timeline.from(product.querySelectorAll('.item'), 0.2, {
        scale: 0.6,
        autoAlpha: 0,
        y: '2.6041666666666665vw',
        stagger: 0.1,
        ease: 'power2.inOut',
      });
      // icon
      // timeline.from(
      //   product.querySelector('.icon'),
      //   0.2,
      //   {
      //     scale: 0.4,
      //     autoAlpha: 0,
      //     transformOrigin: '50% 52%',
      //     ease: 'power2.inOut',
      //   },
      //   0,
      // );
    });
  }

  animateDesigners() {
    gsap.utils.toArray('.designer-elem').forEach((designer) => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          horizontal: true,
          trigger: designer,
          toggleActions: 'play reverse play reverse',
          start: 'left 90%',
          end: 'left 6%',
          markers: false,
          id: designer.getAttribute('id'),
        },
      });

      //  moments & designers & room desc
      timeline.from(
        designer.querySelector('.desc'),
        0.2,
        {
          autoAlpha: 0,
          scale: 0.4,
          y: '-1.0416666666666667vw',
          x: '1.1458333333333333vw',
          transformOrigin: '0 0',
          ease: 'power2.inOut',
        },
        0,
      );
      //  moments & designers & room icon
      timeline.from(
        designer.querySelector('.icon'),
        0.2,
        {
          scale: 0.4,
          // autoAlpha: 0,
          transformOrigin: '50% 54%',
          ease: 'power2.inOut',
        },
        0,
      );
    });
  }

  animateRooms() {
    // animate rooms
    gsap.utils.toArray('.room-elem').forEach((roomElem) => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          horizontal: true,
          trigger: roomElem,
          toggleActions: 'play reverse play reverse',
          start: 'left 92%',
          end: 'left 8%',
          markers: false,
          id: roomElem.getAttribute('id'),
        },
      });
      // room desc
      timeline.from(
        roomElem.querySelector('.desc'),
        0.2,
        {
          autoAlpha: 0,
          scale: 0.4,
          y: '-1.0416666666666667vw',
          x: '1.1458333333333333vw',
          transformOrigin: '0 0',
          ease: 'power2.inOut',
        },
        0,
      );
      // room icon
      // timeline.from(
      //   roomElem.querySelector('.icon'),
      //   0.2,
      //   {
      //     scale: 0.4,
      //     // autoAlpha: 0,
      //     transformOrigin: '50% 52%',
      //     ease: 'power2.inOut',
      //   },
      //   0,
      // );
    });
  }

  animateRoomPreview() {
    // animate room previews
    gsap.utils.toArray('.root-preview-elem').forEach((roomPreview) => {
      const roomPreviewId = roomPreview.getAttribute('id');
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          horizontal: true,
          trigger: roomPreview,
          // element viewport (scroller)
          endTrigger: `#ROOM_${roomPreviewId}`,
          start: '40% 70%',
          end: 'left-=150% center',
          markers: false,
          id: roomPreviewId,
          scrub: true,
        },
      });
      // room preview
      timeline.to(
        roomPreview.querySelector('.room-cropper'),
        {
          left: 0,
        },
        0,
      );
      timeline.to(
        roomPreview.querySelector('.room-expand-icon'),
        {
          autoAlpha: 1,
          scale: 1,
          transformOrigin: 'center center',
        },
        0,
      );
    });
  }

  showDefaultRoomDetails() {
    const rooms = require(`../data/rooms`).default;
    this.openComponentInSpaMode(ELEMENT_TYPE.ROOM, rooms[2040]);
  }

  animateDesignElements() {
    // animate design elem
    gsap.utils.toArray('.design-elem').forEach((designElem) => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          scroller: '.timelineWrapper',
          horizontal: true,
          trigger: designElem,
          toggleActions: 'play reverse play reverse',
          // element viewport (scroller)
          start: 'center 40%',
          end: 'center 50%',
          markers: false,
          scrub: 1, // 1 ?
          id: designElem.getAttribute('id'),
        },
      });
      // step -1
      timeline.set(designElem.querySelector('#useful'), { autoAlpha: 0.25 });
      timeline.set(designElem.querySelector('#build'), { autoAlpha: 0.25 });

      // step 0
      timeline.to(
        designElem.querySelector('.sliding-texts'),
        { marginTop: '-13.020833333333334vw' },
        0,
      );
      timeline.to(designElem.querySelector('#simple'), { autoAlpha: 0.25 }, 0);
      timeline.to(designElem.querySelector('#useful'), { autoAlpha: 1 }, 0);
      timeline.to(designElem.querySelector('#build'), { autoAlpha: 0.25 }, 0);
      // step 1
      timeline.to(
        designElem.querySelector('.sliding-texts'),
        { marginTop: '-26.041666666666668vw', duration: 1 },
        1,
      );
      timeline.to(designElem.querySelector('#simple'), { autoAlpha: 0.25 }, 1);
      timeline.to(designElem.querySelector('#useful'), { autoAlpha: 0.25 }, 1);
      timeline.to(designElem.querySelector('#build'), { autoAlpha: 1 }, 1);
    });
  }

  // enable scroll from mouse
  enableScrollWheel(element) {
    if (typeof document !== 'undefined') {
      const dragTarget = document.querySelector(element);
      dragTarget.addEventListener('wheel', (event) => {
        const normalizedWheel = normalizeWheel(event);
        const isTouchPad = event.wheelDeltaY
          ? event.wheelDeltaY === -3 * event.deltaY
          : event.deltaMode === 0;
        const toLeft = event.deltaY < 0 && dragTarget.scrollLeft > 0;
        const toRight =
          event.deltaY > 0 &&
          dragTarget.scrollLeft <
            dragTarget.scrollWidth - dragTarget.clientWidth;

        if (toLeft || toRight) {
          event.preventDefault();
          if (isTouchPad) {
            gsap.killTweensOf('.timelineWrapper');
            dragTarget.scrollLeft += event.deltaY;
          } else {
            gsap.to('.timelineWrapper', {
              scrollTo: { x: `+=${normalizedWheel.pixelY * 4}` },
              ease: 'power1.easeOut',
              duration: 2,
            });
          }
        }
      });
    }
  }

  enableDrag() {
    if (typeof document !== 'undefined') {
      const slider = document.querySelector('.timelineWrapper');
      let isDown = false;
      let startX;
      let scrollLeft;

      slider.addEventListener('mousedown', (e) => {
        isDown = true;
        slider.classList.add('active');
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      });
      slider.addEventListener('mouseleave', () => {
        isDown = false;
        slider.classList.remove('active');
      });
      slider.addEventListener('mouseup', () => {
        isDown = false;
        slider.classList.remove('active');
      });
      slider.addEventListener('mousemove', (e) => {
        if (!isDown) return;

        e.preventDefault();

        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 5;

        gsap.to(slider, {
          scrollTo: {
            x: `${scrollLeft - walk}`,
          },
          ease: 'power1.easeOut',
          duration: 2,
        });
      });
    }
  }

  render() {
    return (
      <>
        <HeaderBraun className="timeline-header" theme={THEME.BLACK} />
        {/* menu */}
        <Menu
          onClickParent={(isMenuOpened) => {
            this.handleMenu(isMenuOpened);
          }}
          onRoomExplorerClicked={() => this.showDefaultRoomDetails()}
          className="timeline-menu"
        />
        {/* loads compoenents in SPA mode, no Router */}
        {this.state.showSpaComponentLoader && (
          <ComponentLoader
            type={this.state.currentComponentType}
            data={this.state.currentComponentData}
            classesToHide={['.timeline', '.timeline-menu', '.timeline-header']}
            onExitCompleted={() => {
              this.setState({ showSpaComponentLoader: false });
            }}
            from={NAVIGATION_FROM.TIMELINE}
          />
        )}
        <div className={`timelineWrapper ${styles.timelineWrapper}`}>
          {/* timeline */}
          <div className={`timeline ${styles.timeline}`}>
            {/* timeline bar */}
            <div className={`timeline-bar ${styles.timelineBar}`} />
            {/* timeline elements */}
            {timelineData &&
              timelineData.map((element, index) => {
                switch (element?.type) {
                  case ELEMENT_TYPE.DUMMY:
                    return (
                      <div
                        key={index}
                        style={{
                          float: 'left',
                          width: '1px',
                          height: '1px',
                        }}
                        className={styles.timelineElemenent}
                      />
                    );
                  case ELEMENT_TYPE.YEAR:
                    return (
                      <YearComponent
                        key={index}
                        className={`${styles.timelineElemenent} year-elem`}
                        yearClassName="yearNumber"
                        yearIconClassName="yearTimelineIcon"
                        year={element?.year}
                      />
                    );
                  case ELEMENT_TYPE.PRODUCT:
                  case ELEMENT_TYPE.PRODUCT_GROUP:
                    return (
                      <Product
                        key={index}
                        className={`${styles.timelineElemenent} product-elem`}
                        productClassName="item"
                        productTimelineIconClassName="icon"
                        goToProductDetail={(id, data) => {
                          // show component in SPA mode
                          this.openComponentInSpaMode(
                            ELEMENT_TYPE.PRODUCT,
                            data,
                          );
                        }}
                        type={ELEMENT_TYPE.PRODUCT}
                        products={
                          element?.products
                            ? element.products
                            : [{ ...element }]
                        }
                      />
                    );
                  case ELEMENT_TYPE.DESIGNER:
                    return (
                      <Designer
                        key={index}
                        className={`${styles.timelineElemenent} designer-elem`}
                        iconClassName="icon"
                        descriptionClassName="desc"
                        icon={element?.image}
                        id={element?.id}
                        goToDesignerDetail={() => {
                          // show component in SPA mode
                          this.openComponentInSpaMode(
                            ELEMENT_TYPE.DESIGNER,
                            element?.data,
                          );
                        }}
                        title={element?.name}
                        subtitle={element?.position}
                        type={ELEMENT_TYPE.DESIGNER}
                        endYear={this.closestEndYear(element?.between[1])}
                      />
                    );
                  case ELEMENT_TYPE.MOMENT:
                    return (
                      <Moment
                        key={index}
                        id={element?.id}
                        className={`${styles.timelineElemenent} moment-elem`}
                        timelineIconClassName="icon"
                        descriptionClassName="desc"
                        icon={element?.image}
                        goToMomentDetail={() => {
                          // show component in SPA mode
                          this.openComponentInSpaMode(
                            ELEMENT_TYPE.MOMENT,
                            element?.data,
                          );
                        }}
                        title={element?.name}
                        type={ELEMENT_TYPE.MOMENT}
                        subtitle={element?.year}
                      />
                    );
                  case ELEMENT_TYPE.ROOM:
                    return (
                      <Room
                        key={index}
                        id={element?.id}
                        className={`${styles.timelineElemenent} room-elem`}
                        timelineIconClassName="icon"
                        descriptionClassName="desc"
                        title={element?.label}
                        subtitle={element?.action}
                      />
                    );
                  case ELEMENT_TYPE.ROOM_PREVIEW:
                    return (
                      <RoomPreview
                        key={index}
                        id={element?.id}
                        roomPreviewId={element?.roomPreviewId}
                        url={element?.url}
                        goToRoomDetail={() => {
                          // show component in SPA mode
                          this.openComponentInSpaMode(
                            ELEMENT_TYPE.ROOM,
                            element?.data,
                          );
                        }}
                        image={element?.image}
                        type={ELEMENT_TYPE.ROOM}
                        className="root-preview-elem"
                        roomCropClassName="room-cropper"
                        roomExpandIconClassName="room-expand-icon"
                      />
                    );
                  case ELEMENT_TYPE.DESIGN:
                    return (
                      <DesignComponent
                        key={index}
                        id={element.id}
                        className={`${styles.timelineElemenent} design-elem`}
                        slidingTextsClassName="sliding-texts"
                        design={element}
                      />
                    );
                  case ELEMENT_TYPE.DISCOVER:
                    return (
                      <DiscoverComponent
                        key={index}
                        className={`${styles.timelineElemenent} discover-elem`}
                        title={element?.title}
                        subTitle={element?.subTitle}
                        elements={element?.elements}
                      />
                    );
                  default:
                    break;
                }
              })}
          </div>
        </div>
      </>
    );
  }
}
